import React, { useContext, useEffect, useState } from "react";
import { Button, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Select, Tab, Tabs, TextField, Typography } from "@material-ui/core";
import { Add, CheckCircleOutline, Close, DeleteOutline, PlayArrow, Stop } from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { formatMontevideoDateTime } from "../../utils/dateTime";

const initialForm = { title: "", startAt: "", endAt: "", status: "pendiente", notes: "", assigneeId: "", assigneeName: "" };
const labels = { pendiente: "Pendiente", en_progreso: "En progreso", completada: "Completada" };
const TIMER_KEY = "crm-internal-task-timer";
const readTimer = () => { try { return JSON.parse(localStorage.getItem(TIMER_KEY) || "null"); } catch (error) { return null; } };
const formatElapsed = milliseconds => {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  return `${String(Math.floor(total / 3600)).padStart(2, "0")}:${String(Math.floor((total % 3600) / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

const Tasks = () => {
  const { isAuth } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [module, setModule] = useState(0);
  const [controlOpen, setControlOpen] = useState(false);
  const [controlTaskId, setControlTaskId] = useState("");
  const [quickTaskOpen, setQuickTaskOpen] = useState(false);
  const [quickTaskTitle, setQuickTaskTitle] = useState("");
  const [quickTaskSaving, setQuickTaskSaving] = useState(false);
  const [timer, setTimer] = useState(readTimer);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!timer?.startedAt) return undefined;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [timer?.startedAt]);

  const selectedControlTask = tasks.find(task => String(task.id) === String(controlTaskId));
  const elapsed = timer ? Number(timer.elapsed || 0) + (timer.startedAt ? now - Number(timer.startedAt) : 0) : 0;
  const startTimer = () => {
    if (!selectedControlTask) { toast.info("Seleccioná una tarea para iniciar el control."); return; }
    const next = { taskId: selectedControlTask.id, startedAt: Date.now(), elapsed: 0 };
    localStorage.setItem(TIMER_KEY, JSON.stringify(next));
    setTimer(next);
    setNow(Date.now());
  };
  const stopTimer = () => {
    if (!timer) return;
    const next = { ...timer, startedAt: null, elapsed };
    localStorage.setItem(TIMER_KEY, JSON.stringify(next));
    setTimer(next);
  };

  const loadTasks = async () => {
    try {
      const { data } = await api.get("/crm/tasks/");
      setTasks(data.tasks || []);
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => { if (isAuth) loadTasks(); }, [isAuth]);

  useEffect(() => {
    if (!isAuth) return;
    api.get("/crm/task-assignees")
      .then(({ data }) => setUsers(data.users || []))
      .catch(toastError);
  }, [isAuth]);

  const createTask = async event => {
    event.preventDefault();
    if (form.startAt && form.endAt && new Date(form.endAt) < new Date(form.startAt)) {
      toast.info("La fecha de fin debe ser posterior a la fecha de inicio.");
      return;
    }
    try {
      const startAt = form.startAt ? new Date(form.startAt).toISOString() : null;
      const endAt = form.endAt ? new Date(form.endAt).toISOString() : null;
      await api.post("/crm/tasks", { ...form, assigneeId: form.assigneeId || null, startAt, endAt, dueAt: endAt });
      setForm(initialForm);
      toast.success("Tarea creada.");
      await loadTasks();
    } catch (error) {
      toastError(error);
    }
  };

  const createQuickTask = async event => {
    event.preventDefault();
    const title = quickTaskTitle.trim();
    if (!title || quickTaskSaving) return;
    setQuickTaskSaving(true);
    try {
      const { data: createdTask } = await api.post("/crm/tasks", {
        ...initialForm,
        title,
        assigneeId: null,
        startAt: null,
        endAt: null,
        dueAt: null,
      });
      await loadTasks();
      setControlTaskId(createdTask.id);
      setQuickTaskTitle("");
      setQuickTaskOpen(false);
      toast.success("Tarea creada y seleccionada.");
    } catch (error) {
      toastError(error);
    } finally {
      setQuickTaskSaving(false);
    }
  };

  const updateTask = async (task, values) => {
    try {
      await api.put(`/crm/tasks/${task.id}`, { title: task.title, notes: task.notes || "", startAt: task.startAt || null, endAt: task.endAt || task.dueAt || null, dueAt: task.dueAt || task.endAt || null, status: task.status, assigneeId: task.assigneeId || null, assigneeName: task.assigneeName || "", ...values });
      await loadTasks();
    } catch (error) {
      toastError(error);
    }
  };

  const removeTask = async task => {
    if (!window.confirm(`\u00bfEliminar la tarea "${task.title}"?`)) return;
    try {
      await api.delete(`/crm/tasks/${task.id}`);
      await loadTasks();
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <MainContainer>
      <MainHeader><Title>Tareas y agenda</Title></MainHeader>
      <Paper variant="outlined" style={{ marginBottom: 16 }}>
        <Tabs value={module} onChange={(_, value) => value === 1 ? setControlOpen(true) : setModule(value)} indicatorColor="primary" textColor="primary" variant="fullWidth">
          <Tab label="Pendientes por hacer" />
          <Tab label="Control interno" />
        </Tabs>
      </Paper>
      <Dialog open={controlOpen} onClose={() => setControlOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Control interno</Typography>
          <IconButton size="small" onClick={() => setControlOpen(false)} aria-label="Cerrar control interno"><Close /></IconButton>
        </DialogTitle>
        <DialogContent style={{ paddingBottom: 22 }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>Seleccioná la tarea, presioná Inicio y al terminar presioná Fin.</Typography>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <Select
              value={controlTaskId}
              onChange={event => {
                if (event.target.value === "__add_task__") {
                  setQuickTaskOpen(true);
                  return;
                }
                setControlTaskId(event.target.value);
              }}
              displayEmpty
              variant="outlined"
              size="small"
              style={{ minWidth: 230 }}
            >
              <MenuItem value="">Seleccionar tarea</MenuItem>
              <MenuItem value="__add_task__" style={{ color: "#6548f5", fontWeight: 700 }}>
                <Add fontSize="small" style={{ marginRight: 8 }} />
                Agregar nueva tarea
              </MenuItem>
              {tasks.filter(task => task.status !== "completada").map(task => <MenuItem key={task.id} value={task.id}>{task.title}</MenuItem>)}
            </Select>
            {!timer?.startedAt ? <Button startIcon={<PlayArrow />} color="primary" variant="contained" onClick={startTimer}>Inicio</Button> : <Button startIcon={<Stop />} color="secondary" variant="contained" onClick={stopTimer}>Fin</Button>}
            <Typography variant="h6" style={{ fontVariantNumeric: "tabular-nums" }}>{formatElapsed(elapsed)}</Typography>
          </div>
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 12 }}>El tiempo queda guardado en este equipo y puede reanudarse sin perder lo acumulado.</Typography>
        </DialogContent>
      </Dialog>
      <Dialog open={quickTaskOpen} onClose={() => !quickTaskSaving && setQuickTaskOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Agregar tarea</Typography>
          <IconButton size="small" disabled={quickTaskSaving} onClick={() => setQuickTaskOpen(false)} aria-label="Cerrar nueva tarea"><Close /></IconButton>
        </DialogTitle>
        <DialogContent style={{ paddingBottom: 22 }}>
          <form onSubmit={createQuickTask}>
            <TextField
              autoFocus
              required
              fullWidth
              label="Nombre de la tarea"
              value={quickTaskTitle}
              onChange={event => setQuickTaskTitle(event.target.value)}
              variant="outlined"
              disabled={quickTaskSaving}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <Button disabled={quickTaskSaving} onClick={() => setQuickTaskOpen(false)}>Cancelar</Button>
              <Button type="submit" color="primary" variant="contained" startIcon={<Add />} disabled={!quickTaskTitle.trim() || quickTaskSaving}>
                {quickTaskSaving ? "Guardando..." : "Agregar y seleccionar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <Paper style={{ padding: 16, marginBottom: 16 }} variant="outlined">
        <Typography variant="subtitle1" style={{ marginBottom: 10 }}>Agregar seguimiento</Typography>
        <form onSubmit={createTask} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
          <TextField required fullWidth label="Tarea" value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} variant="outlined" size="small" />
          <TextField fullWidth label="Fecha de inicio" type="datetime-local" InputLabelProps={{ shrink: true }} value={form.startAt} onChange={event => setForm({ ...form, startAt: event.target.value })} variant="outlined" size="small" />
          <TextField fullWidth label="Fecha de fin" type="datetime-local" InputLabelProps={{ shrink: true }} value={form.endAt} onChange={event => setForm({ ...form, endAt: event.target.value })} variant="outlined" size="small" />
          <TextField fullWidth label="Notas" value={form.notes} onChange={event => setForm({ ...form, notes: event.target.value })} variant="outlined" size="small" />
          <TextField
            select
            fullWidth
            label="Asignar a"
            value={form.assigneeId}
            onChange={event => {
              const person = users.find(user => String(user.id) === String(event.target.value));
              setForm({ ...form, assigneeId: event.target.value, assigneeName: person?.name || "" });
            }}
            variant="outlined"
            size="small"
          >
            <MenuItem value="">Sin asignar</MenuItem>
            {users.map(person => <MenuItem key={person.id} value={person.id}>{person.name}{person.profile === "admin" ? " (Administrador)" : ""}</MenuItem>)}
          </TextField>
          <Button type="submit" color="primary" variant="contained" style={{ justifySelf: "start" }}>Agregar</Button>
        </form>
      </Paper>
      {module === 0 && <Paper variant="outlined" style={{ flex: 1, overflow: "auto" }}>
        {tasks.filter(task => task.status !== "completada").map(task => (
          <div key={task.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, borderBottom: "1px solid #e5e7eb", flexWrap: "wrap" }}>
            <CheckCircleOutline color={task.status === "completada" ? "primary" : "disabled"} />
            <div style={{ flex: 1, minWidth: 180, overflowWrap: "anywhere" }}>
              <Typography style={{ textDecoration: task.status === "completada" ? "line-through" : "none" }}>{task.title}</Typography>
              <Typography variant="caption" color="textSecondary" style={{ overflowWrap: "anywhere" }}>{task.startAt ? `Inicio: ${formatMontevideoDateTime(task.startAt)} · ` : ""}{(task.endAt || task.dueAt) ? `Fin: ${formatMontevideoDateTime(task.endAt || task.dueAt)}` : "Sin fecha"}{task.notes ? ` · ${task.notes}` : ""}</Typography>
              <Typography variant="caption" display="block" color="textSecondary">{task.assigneeName ? `Responsable: ${task.assigneeName}` : "Sin responsable asignado"}</Typography>
            </div>
            <Select
              value={task.assigneeId || ""}
              onChange={event => {
                const person = users.find(user => String(user.id) === String(event.target.value));
                updateTask(task, { assigneeId: event.target.value || null, assigneeName: person?.name || "" });
              }}
              variant="outlined"
              size="small"
              displayEmpty
            >
              <MenuItem value="">Sin asignar</MenuItem>
              {users.map(person => <MenuItem key={person.id} value={person.id}>{person.name}</MenuItem>)}
            </Select>
            <Select value={task.status} onChange={event => updateTask(task, { status: event.target.value })} variant="outlined" size="small">
              {Object.entries(labels).map(([value, text]) => <MenuItem key={value} value={value}>{text}</MenuItem>)}
            </Select>
            <IconButton aria-label={`Eliminar ${task.title}`} onClick={() => removeTask(task)}><DeleteOutline /></IconButton>
          </div>
        ))}
        {!tasks.length && <Typography align="center" color="textSecondary" style={{ padding: 32 }}>No hay tareas pendientes.</Typography>}
      </Paper>}
      {timer && <div style={{ position: "fixed", right: 18, bottom: 18, zIndex: 1400, background: "#21133f", color: "#fff", borderRadius: 12, padding: "10px 14px", boxShadow: "0 8px 24px rgba(0,0,0,.25)", minWidth: 190 }}>
        <Typography variant="caption" style={{ color: "#d9ccff", display: "block" }}>Control interno</Typography>
        <Typography noWrap style={{ fontWeight: 700, maxWidth: 220 }}>{tasks.find(task => String(task.id) === String(timer.taskId))?.title || "Tarea"}</Typography>
        <Typography variant="h6" style={{ fontVariantNumeric: "tabular-nums" }}>{formatElapsed(elapsed)}</Typography>
        {timer.startedAt && <Button size="small" startIcon={<Stop />} onClick={stopTimer} style={{ color: "#fff", padding: 0 }}>Finalizar</Button>}
      </div>}
    </MainContainer>
  );
};

export default Tasks;
